import {
  Mesh,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector2,
} from "three";

import { SkeletalPosition } from "mannequin/src/types/tracking/raw";

import { Frames, TaggedNeighbor } from "../../../util/frames";
import { calculateAlpha } from "../../../util/clock";
import { JointPositions, TrackingSkeleton } from "./tracking-skeleton";
import { ActorTrack } from "../add-ons/track";
import { TrackLabel } from "./label";
import { Context } from "../../../context";

interface CentroidFrame {
  position: TaggedNeighbor<SkeletalPosition>;
  joints: JointPositions;
}

export class RawHelper extends Object3D {
  skeleton: TrackingSkeleton;
  private track: ActorTrack;
  private label: TrackLabel;

  private color: number;

  private jointPositions: JointPositions = new JointPositions();
  private trackId: number;
  private positionId: number;

  private centroid: Mesh;

  private prev?: CentroidFrame;
  private next?: CentroidFrame;

  private prevFrameNumSeen = -1;
  private prevFrameNum?: number;

  private contextRef: Context;

  constructor(position: SkeletalPosition, context: Context) {
    super();

    this.color = Math.random() * 0xffffff;

    this.skeleton = new TrackingSkeleton();
    this.add(this.skeleton);

    const rendererSize = new Vector2();
    context.renderer.getSize(rendererSize);
    this.track = new ActorTrack(
      position.track_id,
      rendererSize,
      0.5,
      this.color
    );
    this.add(this.track);

    this.trackId = position.track_id;
    this.positionId = position.positionId;

    const ball = new SphereGeometry(1, 32, 8);
    const mat = new MeshBasicMaterial({ color: this.color });
    this.centroid = new Mesh(ball, mat);
    this.add(this.centroid);

    this.contextRef = context;

    this.label = this.makeLabel(position, this.color);
  }

  private makeLabel(position: SkeletalPosition, color: number): TrackLabel {
    this.label = new TrackLabel(
      `${position.track_id}/${position.positionId}`,
      // @ts-ignore
      this.contextRef.playerFont,
      color
    );
    this.label.scale.setScalar(0.03);
    this.label.position.set(2, 2, 0);
    this.centroid.add(this.label);

    return this.label;
  }

  update(frames: Frames, time: number) {
    // see if we've got a new frame of data
    const newFrame =
      frames.current && this.prevFrameNumSeen != frames.current.num;
    if (newFrame) {
      const neighbors = frames.getNeighbours<SkeletalPosition>((frame) => {
        return frame.rawJoints
          ? frame.rawJoints.find(
              (position) => position.track_id == this.trackId
            )
          : undefined;
      });

      if (neighbors?.prev) {
        this.prevFrameNum = neighbors.prev.num;
        this.prev = {
          position: neighbors.prev,
          joints: this.prev
            ? this.prev.joints.updateFromSkeletalPosition(
                neighbors.prev.neighbor
              )
            : JointPositions.fromSkeletalPosition(neighbors.prev.neighbor),
        };
      }
      if (neighbors?.next) {
        const position = neighbors.next.neighbor;
        if (this.positionId !== position.positionId) {
          this.positionId = position.positionId;

          // DEBUG: bring back for debug
          // this.label.position.y = Math.random() * 20 + 10;
          // this.parent?.attach(this.label);
          // this.makeLabel(position, this.color);
        }

        this.next = {
          position: neighbors.next,
          joints: this.next
            ? this.next.joints.updateFromSkeletalPosition(
                neighbors.next.neighbor
              )
            : JointPositions.fromSkeletalPosition(neighbors.next.neighbor),
        };
      }

      if (frames.current?.num) this.prevFrameNumSeen = frames.current?.num;
    }

    if (!this.prev?.position || !this.next?.position) return;

    const interpAlpha = calculateAlpha(
      [this.prev.position.time, this.next.position.time],
      time
    );
    if (interpAlpha == undefined) return false;

    this.jointPositions.interpolate(
      this.prev.joints,
      this.next.joints,
      interpAlpha
    );

    this.jointPositions.getCentroid(this.centroid.position);
    this.centroid.position.y = 0;
    if (newFrame) this.track.update(this.centroid.position);
    this.skeleton.update(this.jointPositions);
  }

  ageFrom(frameNum: number): number {
    return this.prevFrameNum !== undefined
      ? frameNum - this.prevFrameNum
      : Infinity;
  }
}
